import { Injectable, computed, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * What the device says about notifications for this app.
 *
 * `unsupported` is ours, not the plugin's: the web build has no native
 * permission to ask for, and every caller has to handle that case anyway.
 * The rest are Capacitor's own `PermissionState` values.
 */
export type NotificationState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/**
 * The device's notification permission - one seam, so the guard and the page
 * ask the same question the same way, and tests can answer it without a
 * device (Angular's harness forbids `vi.mock` on relative imports, hence an
 * injectable rather than bare functions - the same reasoning as ClubJoin).
 *
 * Scope on purpose: this asks for PERMISSION and nothing else. Registering
 * with APNs/FCM for a token, and the delivery that follows, is a later job
 * (it needs the Push Notifications capability in Xcode and the Firebase
 * sender set up); the system prompt itself needs none of that.
 *
 * `prompt-with-rationale` (Android's "ask again") is folded into `prompt`:
 * both mean the system will still show the sheet, which is the only
 * distinction any caller here cares about.
 */
@Injectable({ providedIn: 'root' })
export class NotificationAccess {
  /** Native builds only - the web app has no device permission to ask for. */
  readonly supported =
    Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('PushNotifications');

  private readonly _skipped = signal(false);
  /**
   * They said "not now" this run. Held in memory, deliberately: skipping is
   * for THIS session, and the ask comes back on the next launch - but it
   * must not come back on the next navigation, which is what the guard
   * would otherwise do the moment the page sends them onward.
   */
  readonly skipped = this._skipped.asReadonly();

  /** What the device says right now, without asking the member anything. */
  async check(): Promise<NotificationState> {
    if (!this.supported) return 'unsupported';
    try {
      return normalise((await PushNotifications.checkPermissions()).receive);
    } catch {
      // A plugin that cannot answer is not a reason to hold up the app.
      return 'unsupported';
    }
  }

  /** Put the system's own prompt on screen and report what came back.
   *  Feeds {@link status}, so the landing decision sees the fresh answer. */
  async request(): Promise<NotificationState> {
    if (!this.supported) return 'unsupported';
    let state: NotificationState;
    try {
      state = normalise((await PushNotifications.requestPermissions()).receive);
    } catch {
      state = 'unsupported';
    }
    this._status.set(state);
    return state;
  }

  /** Not now - stop asking for the rest of this session. */
  skip(): void {
    this._skipped.set(true);
  }

  private readonly _status = signal<NotificationState | undefined>(undefined);
  /** The settled device answer - undefined until {@link whenChecked} has
   *  run once. `request()` updates it, so the landing decision that reads
   *  it never re-asks a question the member just answered. */
  readonly status = this._status.asReadonly();

  /** The ask is still worth making: the device would show its sheet and
   *  the member has not waved it away this session. THE landing question -
   *  false on web, after a grant/denial, and after "not now". */
  readonly shouldAsk = computed(() => this._status() === 'prompt' && !this._skipped());

  private checking: Promise<void> | null = null;

  /**
   * The data-init loader (data-init.ts): settle {@link status} once per
   * app run. Imperative + idempotent, settles instantly on the web
   * ('unsupported'), and a throwing plugin reads as unsupported - the
   * check can never hold the boot veil.
   *
   * Device-level, not per-uid: an account switch does not re-check, which
   * is right - the permission belongs to the APP on this device.
   */
  whenChecked(): Promise<void> {
    if (this._status() !== undefined) return Promise.resolve();
    this.checking ??= this.check()
      .then((state) => this._status.set(state))
      .finally(() => {
        this.checking = null;
      });
    return this.checking;
  }
}

function normalise(state: string): NotificationState {
  if (state === 'granted') return 'granted';
  if (state === 'denied') return 'denied';
  return 'prompt';
}
